/**
 * 統計画面用の軽量 SVG チャート（外部ライブラリなし）
 */

function formatChartDateLabel(targetDateKey) {
  const text = String(targetDateKey || '');
  if (/^\d{8}$/.test(text)) {
    return `${text.slice(4, 6)}/${text.slice(6, 8)}`;
  }
  return text.slice(-2) || '';
}

export function MiniBarChart({ points = [], width = 132, height = 44, className = '' }) {
  const values = points.map((point) => Number(point.value || 0));
  const max = Math.max(...values, 1);
  const slotWidth = width / Math.max(values.length, 1);

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`stats-mini-chart stats-mini-chart--bar ${className}`.trim()}
      aria-hidden="true"
    >
      {values.map((value, index) => {
        const barHeight = Math.max(2, (value / max) * (height - 6));
        const x = index * slotWidth + 1;
        const y = height - barHeight - 2;
        return (
          <rect
            key={`bar-${index}`}
            x={x}
            y={y}
            width={Math.max(slotWidth - 2, 2)}
            height={barHeight}
            rx="1"
          />
        );
      })}
    </svg>
  );
}

export function MiniLineChart({ points = [], width = 132, height = 44, className = '' }) {
  const values = points.map((point) => Number(point.value || 0));
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const stepX = values.length > 1 ? width / (values.length - 1) : width;

  const polylinePoints = values.map((value, index) => {
    const x = index * stepX;
    const normalized = (value - min) / span;
    const y = height - 4 - normalized * (height - 8);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`stats-mini-chart stats-mini-chart--line ${className}`.trim()}
      aria-hidden="true"
    >
      <polyline points={polylinePoints} fill="none" />
      {values.map((value, index) => {
        const x = index * stepX;
        const normalized = (value - min) / span;
        const y = height - 4 - normalized * (height - 8);
        return <circle key={`dot-${index}`} cx={x} cy={y} r="2" />;
      })}
    </svg>
  );
}

export function DetailBarChart({ points = [], width = 720, height = 240 }) {
  const values = points.map((point) => Number(point.value || 0));
  const max = Math.max(...values, 1);
  const slotWidth = width / Math.max(values.length, 1);
  const chartHeight = height - 28;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="stats-detail-chart stats-detail-chart--bar"
      role="img"
      aria-label="棒グラフ"
    >
      {values.map((value, index) => {
        const barHeight = Math.max(2, (value / max) * (chartHeight - 8));
        const x = index * slotWidth + 4;
        const y = chartHeight - barHeight;
        return (
          <g key={`detail-bar-${index}`}>
            <rect
              x={x}
              y={y}
              width={Math.max(slotWidth - 8, 4)}
              height={barHeight}
              rx="2"
            />
            <text x={x + (slotWidth - 8) / 2} y={height - 6} textAnchor="middle">
              {formatChartDateLabel(points[index]?.targetDateKey)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function DetailLineChart({ points = [], width = 720, height = 240 }) {
  const values = points.map((point) => Number(point.value || 0));
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = Math.max(max - min, 1);
  const chartHeight = height - 28;
  const stepX = values.length > 1 ? (width - 16) / (values.length - 1) : 0;

  const polylinePoints = values.map((value, index) => {
    const x = 8 + index * stepX;
    const normalized = (value - min) / span;
    const y = chartHeight - normalized * (chartHeight - 8);
    return `${x},${y}`;
  }).join(' ');

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="stats-detail-chart stats-detail-chart--line"
      role="img"
      aria-label="折れ線グラフ"
    >
      <polyline points={polylinePoints} fill="none" />
      {values.map((value, index) => {
        const x = 8 + index * stepX;
        const normalized = (value - min) / span;
        const y = chartHeight - normalized * (chartHeight - 8);
        return (
          <g key={`detail-dot-${index}`}>
            <circle cx={x} cy={y} r="3" />
            <text x={x} y={height - 6} textAnchor="middle">
              {formatChartDateLabel(points[index]?.targetDateKey)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
